import * as React from "react";

import { NotImplementedError } from "../../../../../Common/Errors";
import { noop } from "../../../../../Common/TypingUtils";
import { Guid } from "../../../Engine/Types/Guid";

export type ComponentPageObject = unknown;
interface RegistryPageObject {
    correlationId: Guid;
    componentRef: React.RefObject<ComponentPageObject>;
}

export interface ITestPageObjectsRegistry {
    registerPageObjectHandler(id: string, correlationId: Guid, reactRef: React.RefObject<ComponentPageObject>): void;
    unregisterPageObjectHandler(id: string, correlationId: Guid, reactRef: React.RefObject<ComponentPageObject>): void;
    getPageObject(id: string): unknown;
}

export class TestPageObjectsRegistry implements ITestPageObjectsRegistry {
    private readonly pageObjects: Map<string, RegistryPageObject> = new Map();

    public registerPageObjectHandler(
        id: string,
        correlationId: Guid,
        control: React.RefObject<ComponentPageObject>
    ): void {
        const normalizedId = id.replace(/\/\*$/g, "");
        const existingPageObject = this.pageObjects.get(normalizedId);
        if (!existingPageObject) {
            this.pageObjects.set(normalizedId, { correlationId: correlationId, componentRef: control });
        }
    }

    public unregisterPageObjectHandler(id: string, correlationId: Guid): void {
        const normalizedId = id.replace(/\/\*$/g, "");
        const existingPageObject = this.pageObjects.get(normalizedId);
        if (existingPageObject?.correlationId === correlationId) {
            this.pageObjects.delete(normalizedId);
        }
    }

    public getPageObject(id: string): ComponentPageObject {
        return this.pageObjects.get(id)?.componentRef.current;
    }
}

const TestPageObjectsRegistryContext = React.createContext<ITestPageObjectsRegistry>({
    registerPageObjectHandler: noop,
    unregisterPageObjectHandler: noop,
    getPageObject: () => {
        throw new NotImplementedError();
    },
});

export function useTestPageObjectsRegistry(): ITestPageObjectsRegistry {
    return React.useContext(TestPageObjectsRegistryContext);
}

interface TestPageObjectsRegistryContextProviderProps {
    children: React.ReactNode;
}

export function TestPageObjectsRegistryContextProvider(
    props: TestPageObjectsRegistryContextProviderProps
): JSX.Element {
    const [pageObjectRegistry] = React.useState<TestPageObjectsRegistry>(() => new TestPageObjectsRegistry());

    React.useEffect(() => {
        window.__CANDY__pageObjectRegistry = pageObjectRegistry;
        return () => {
            window.__CANDY__pageObjectRegistry = undefined;
        };
    }, []);

    return (
        <TestPageObjectsRegistryContext.Provider value={pageObjectRegistry}>
            {props.children}
        </TestPageObjectsRegistryContext.Provider>
    );
}
